import { formatPersonName } from '@/utils/personName';
/**
 * EstatePersonDialog — Estate entity entry point for the RAO people form.
 *
 * Composition root: mounts the Estate section suite on top of the shared modal
 * shell. Mirrors IndividualPersonDialog's structure but OMITS:
 *   - Citizenship section (no decedent citizenship on estate CIF)
 *   - Employment section (estate doesn't employ)
 *   - CIP scan panel (probate court filing is the CIP source, not an ID scan)
 *
 * Renders, top → bottom:
 *   1. EstateIdentitySection — Estate Name, Decedent, DoD, EIN, etc.
 *   2. EstateCipSection       — Letters variant, case fields, Case.Net lookup
 *   3. AddressSection (shared) — Estate mailing address
 *   4. ContactSection (shared) — PR phone/email
 *   5. DocumentSlotsSection (shared) — required uploads
 *       (Letters Testamentary / Letters of Administration / Death Certificate /
 *        EIN Letter / CIP_IND on Personal Rep per seed rules)
 *
 * Supports readOnly mode identical to IndividualPersonDialog. All entries
 * are modal (the inline 'existing-cif-maint' surface was retired 2026-04-24).
 *
 * Card reference: docs/port-design/category-cards/estate.md
 */

import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { ModalShell } from '@/components/primitives';
import type { RaoPerson } from '../../../types';
import { EstateIdentitySection } from './EstateIdentitySection';
import { EstateCipSection } from './EstateCipSection';
import { useEstateDerivation } from './useEstateDerivation';
import type { EstateLettersVariant } from './estate-config';

// Shared sections
import { AddressSection } from '../shared/AddressSection';
import { ContactSection } from '../shared/ContactSection';
import { DocumentSlotsSection } from '../shared/DocumentSlotsSection';
import { PdwFooterDyad } from '../shared/PdwFooterDyad';
import {
  type AttachedDoc,
  readAttachedDocs,
  mergeAttachedDoc,
  mergeAttachedDocsKeepExisting,
} from '../shared/attachedDocs';
import { useAttachedDocsHydration } from '../shared/useInquiryHydration';
import { EntityConvergedShell } from '../EntityConvergedShell';
import type { PdwTabKey } from '../PdwTabStrip';

// -----------------------------------------------------------------------------
// Props — parallel to IndividualPersonDialog
// -----------------------------------------------------------------------------

export type EstatePersonDialogMode = 'review-add' | 'new' | 'edit';

export interface EstatePersonDialogProps {
  open: boolean;
  mode: EstatePersonDialogMode;
  person: RaoPerson;
  onSave: (person: RaoPerson) => void;
  onClose: () => void;
  cartCifIds?: ReadonlyArray<string>;
  cartId: string;
  officerCode?: string;
  readOnly?: boolean;
}

// -----------------------------------------------------------------------------
// Component
// -----------------------------------------------------------------------------

export function EstatePersonDialog({
  open,
  mode,
  person,
  onSave,
  onClose,
  cartCifIds,
  cartId,
  officerCode,
  readOnly = false,
}: EstatePersonDialogProps): JSX.Element | null {
  const [local, setLocal] = useState<RaoPerson>(person);
  const [hasChanges, setHasChanges] = useState<boolean>(false);

  // Slot-4 T0 (Contract C2) — attachedDocs live on the REAL draft
  // (local.extra.attachedDocs); useEstateDerivation gates Save on Letters-
  // uploaded presence and they now survive save + reopen.
  const attachedDocs = useMemo<ReadonlyArray<AttachedDoc>>(() => readAttachedDocs(local), [local]);

  const registerAttachedDoc = useCallback((doc: AttachedDoc): void => {
    setLocal((prev) => mergeAttachedDoc(prev, doc));
    setHasChanges(true);
  }, []);

  useEffect(() => {
    setLocal(person);
    setHasChanges(false);
  }, [person]);

  // Slot-4 T5 — Edit-mode doc hydration from the v3 Document Library.
  const { fetchAttachedDocsForCif } = useAttachedDocsHydration();
  const hydratedCifRef = useRef<string | null>(null);
  useEffect(() => {
    const cifId = (person.cifId ?? '').trim();
    if (mode === 'new' || !cifId || hydratedCifRef.current === cifId) return;
    hydratedCifRef.current = cifId;
    let cancelled = false;
    void (async () => {
      const docs = await fetchAttachedDocsForCif(cifId);
      if (cancelled || docs.length === 0) return;
      setLocal((prev) => mergeAttachedDocsKeepExisting(prev, docs));
    })();
    return () => {
      cancelled = true;
    };
  }, [person.cifId, mode, fetchAttachedDocsForCif]);

  const sectionMode: 'new' | 'existing' = mode === 'new' ? 'new' : 'existing';

  // Pull variant out of extra for the derivation hook.
  const variant: EstateLettersVariant | undefined =
    typeof local.extra?.lettersVariant === 'string'
      ? (local.extra.lettersVariant as EstateLettersVariant)
      : undefined;

  const derivation = useEstateDerivation(local, {
    mode: sectionMode,
    storedType: person.customerType,
    lettersVariant: variant,
    attachedDocs,
  });

  const update = useCallback((patch: Partial<RaoPerson>): void => {
    setLocal((prev) => {
      const next: RaoPerson = { ...prev, ...patch };
      // Keep fullName display in sync for downstream cart rendering. For
      // Estate this is "[Estate of Decedent]" built from first + last.
      const fn = next.firstName ?? '';
      const ln = next.lastName ?? '';
      const display = [fn, ln].filter((x) => !!x).join(' ').trim();
      if (display) next.fullName = display;
      if (prev.mode === 'existing') next.dirty = true;
      return next;
    });
    setHasChanges(true);
  }, []);

  const isChanged = useCallback(
    (field: string): boolean => {
      // Deep-ish compare for extra.* fields (case number, state, etc.)
      if (
        field === 'idNumber' ||
        field === 'idState' ||
        field === 'idCounty' ||
        field === 'idIssueDate' ||
        field === 'idExpiryDate' ||
        field === 'decedentName' ||
        field === 'executorName' ||
        field === 'executorCifId'
      ) {
        const a = (local.extra as Record<string, unknown> | undefined)?.[field];
        const b = (person.extra as Record<string, unknown> | undefined)?.[field];
        return a !== b && !(a == null && b == null);
      }
      const a = (local as unknown as Record<string, unknown>)[field];
      const b = (person as unknown as Record<string, unknown>)[field];
      return a !== b && !(a == null && b == null);
    },
    [local, person],
  );

  const displayName =
    local.firstName || local.lastName
      ? formatPersonName(local)
      : 'Estate';

  const title =
    mode === 'review-add'
      ? `Review & add — ${displayName}`
      : mode === 'new'
        ? 'Add new estate'
        : `Edit estate — ${displayName}`;

  const btnLabel =
    mode === 'review-add'
      ? hasChanges ? 'Add existing w/ changes' : 'Add existing'
      : mode === 'new'
        ? 'Add new'
        : 'Save changes';

  const dupInCart =
    !!(local.cifId && cartCifIds?.includes(local.cifId)) && mode === 'review-add';
  const saveDisabled = dupInCart || !!derivation.requiresSupervisorApproval;

  // BACKLOG-0016 — sticky-footer Return/Continue dyad (flat-scroll dialog).
  const continueDisabledReason = dupInCart
    ? `CIF ${local.cifId ?? ''} is already on this cart.`
    : derivation.requiresSupervisorApproval
      ? 'Supervisor approval required before saving.'
      : undefined;

  // ---------------------------------------------------------------------------
  // Sections grouped by canonical tab (D3 convergence). Estate surfaces:
  // Profile (estate name / decedent / DoD / EIN) · CIP (Letters variant + case
  // fields + Case.Net) · Contact · Documents (Letters / Death Cert / EIN).
  // ---------------------------------------------------------------------------
  const sectionsByTab: Partial<Record<PdwTabKey, JSX.Element>> = {
    profile: (
      <EstateIdentitySection
        person={local}
        onChange={update}
        mode={sectionMode}
        isChanged={isChanged}
        derivation={derivation}
        readOnly={readOnly}
      />
    ),
    cip: (
      <EstateCipSection
        person={local}
        onChange={update}
        isChanged={isChanged}
        readOnly={readOnly}
      />
    ),
    contact: (
      <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
        <AddressSection person={local} onChange={update} isChanged={isChanged} readOnly={readOnly} />
        <ContactSection person={local} onChange={update} isChanged={isChanged} readOnly={readOnly} />
      </div>
    ),
    documents: (
      <DocumentSlotsSection
        person={local}
        cartId={cartId}
        officerCode={officerCode ?? 'UNKNOWN'}
        entityType={derivation.customerType}
        attachedDocs={attachedDocs}
        onDocumentRegistered={registerAttachedDoc}
        readOnly={readOnly}
      />
    ),
  };

  // Modal surface — every entry into the dialog (New Acct + Maint CIF).
  return (
    <ModalShell open={open} onClose={onClose} title={title} size="lg">
      <EntityConvergedShell
        person={local}
        mode={mode}
        dupInCart={dupInCart}
        tabs={['review', 'profile', 'cip', 'contact', 'documents']}
        sectionsByTab={sectionsByTab}
        onAttachedDocsChange={(docs) => docs.forEach(registerAttachedDoc)}
        onPersonPatch={update}
        cartId={cartId}
        officerCode={officerCode ?? 'UNKNOWN'}
        readOnly={readOnly}
      />

      <PdwFooterDyad
        currentStep={0}
        totalSteps={1}
        onReturn={onClose}
        onContinue={() => onSave(local)}
        continueDisabled={saveDisabled || readOnly}
        continueDisabledReason={continueDisabledReason}
        lastStepLabel={`${btnLabel} →`}
      />
    </ModalShell>
  );
}
